/**
 * Wishlist with a swappable storage adapter.
 *
 * Today items live in localStorage (LocalWishlistStore). A future adapter
 * (customer metafields through an app proxy, or a wishlist app) only has to
 * implement the same async list/add/remove methods and be passed to
 * wishlist.useStore(). Everything else listens to "theme:wishlist:updated".
 */
import { events, storage, announce } from '@theme/core';

const KEY = 'theme:wishlist';
const MAX_ITEMS = 100;

export class LocalWishlistStore {
  async list() {
    const items = storage.get(KEY, []);
    return Array.isArray(items) ? items : [];
  }

  async add(handle) {
    const items = (await this.list()).filter((item) => item !== handle);
    items.unshift(handle);
    storage.set(KEY, items.slice(0, MAX_ITEMS));
  }

  async remove(handle) {
    storage.set(
      KEY,
      (await this.list()).filter((item) => item !== handle),
    );
  }
}

const strings = window.Theme?.strings || {};

export const wishlist = {
  store: new LocalWishlistStore(),
  cache: null,

  useStore(store) {
    this.store = store;
    this.cache = null;
    return this.refresh();
  },

  async list() {
    if (!this.cache) this.cache = await this.store.list();
    return this.cache;
  },

  async has(handle) {
    return (await this.list()).includes(handle);
  },

  async toggle(handle) {
    const saved = await this.has(handle);
    if (saved) await this.store.remove(handle);
    else await this.store.add(handle);
    await this.refresh();
    announce(saved ? strings.wishlistRemoved : strings.wishlistAdded);
    return !saved;
  },

  async refresh() {
    this.cache = await this.store.list();
    events.emit('wishlist:updated', { items: this.cache });
    return this.cache;
  },
};

window.Theme = Object.assign(window.Theme || {}, { wishlist });

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;

    this.button.addEventListener('click', async (event) => {
      event.preventDefault();
      event.stopPropagation();
      const saved = await wishlist.toggle(this.dataset.handle);
      this.#render(saved);
    });

    this.off = events.on('wishlist:updated', ({ items }) => this.#render(items.includes(this.dataset.handle)));
    wishlist.has(this.dataset.handle).then((saved) => this.#render(saved));
  }

  disconnectedCallback() {
    this.off?.();
  }

  #render(saved) {
    this.button.setAttribute('aria-pressed', String(saved));
  }
}

if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);

/** Keeps every [data-wishlist-count] element (e.g. the header) in sync. */
function syncCounts(items) {
  document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
    el.textContent = items.length;
    el.hidden = items.length === 0;
  });
}

events.on('wishlist:updated', ({ items }) => syncCounts(items));
wishlist.list().then(syncCounts);

// Other tabs changing the wishlist
window.addEventListener('storage', (event) => {
  if (event.key === KEY) wishlist.refresh();
});
